import type { ReactNode } from 'react';

/*
 * Some useful types not tied to specific components.
 */

/**
 * For props where we use a string most of the time but need the ability to pass arbitrary JSX for formatting etc.
 */
export type FormattedString = ReactNode;

export type TagType = 'none' | 'required' | 'optional' | 'recommended';

export type TagVariant = 'default' | 'highlight' | 'notification' | 'success';

/** Shared prop interface for rendering a tag element within an input option (e.g radio) */
export interface CustomTagProps {
  variant?: TagVariant;
  label: string;
}

/** Props that apply to any input type: RCTextInput, RCCheckbox, RCSelect, RCRadio, etc */
export interface CommonInputProps {
  /** Passed through to the applicable HTML tag. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /** This should be a sentence-case string (or, rarely, a ReactNode) without ending punctuation; try to keep it concise (under ~100 characters). */
  label: FormattedString;

  /** Passed through to the applicable HTML tag. */
  id?: string;

  /** Passed through to the applicable HTML tag. */
  name?: string;

  /** Passed through to the applicable HTML tag. */
  disabled?: boolean;

  /** Signals to browser's form validator that a non-empty value must be selected */
  required?: boolean;

  /** Validation error message. Sentence-case without ending punctuation. Keep this terse as it should fit in the space below the input itself. */
  errorText?: string;

  /** If a field requires additional guiding text, use this; follow the same copy guidelines as `label` */
  helpText?: FormattedString;

  /** Renders an `RCTag` next to the label.
   * This is __unrelated__ to whether the field is actually `required`;
   * depending on the form, you may want to call out all optional fields or all required fields –
   * whichever is the exception to the rest of the form. */
  tag?: TagType;
}

export type AlertType = 'info' | 'warning' | 'error' | 'success' | 'highlight' | 'plain';

export type BannerType = 'info' | 'warning' | 'error' | 'success' | 'highlight' | 'plain';

export type ToastCloseType = 'timeout' | 'dismiss' | 'swipe';

export enum ThemeType {
  light = 'light',
  dark = 'dark',
  default = light,
}
